import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider, useAuth } from "./lib/auth";
import { usePublicConfig } from "./lib/publicConfig";
import { ImpersonationBanner } from "./components/AdminLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import Home from "./pages/Home";
import Automotive from "./pages/Automotive";
import Entrepreneurs from "./pages/Entrepreneurs";
import Business from "./pages/Business";
import Pricing from "./pages/Pricing";
import Courses from "./pages/Courses";
import CategoryPage from "./pages/CategoryPage";
import CourseDetail from "./pages/CourseDetail";
import CourseComingSoon from "./pages/CourseComingSoon";
import FreePreview from "./pages/FreePreview";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import InstructorProfile from "./pages/InstructorProfile";
import NotFound from "./pages/NotFound";

import Dashboard from "./pages/Dashboard";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancelled from "./pages/CheckoutCancelled";
import CheckoutUpsell from "./pages/CheckoutUpsell";
import CoursePlayer from "./pages/CoursePlayer";
import CourseComplete from "./pages/CourseComplete";
import CertificateShare from "./pages/CertificateShare";

import VerifyCertificate from "./pages/VerifyCertificate";
import Leaderboard from "./pages/Leaderboard";
import Achievements from "./pages/Achievements";
import Certificates from "./pages/Certificates";
import MyCourses from "./pages/MyCourses";

// Phase 4
import Community from "./pages/Community";
import Internal from "./pages/Internal";
import CommunityPost from "./pages/CommunityPost";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";

// Phase 7: code-split admin + instructor heavy bundles
const AdminAssessments       = lazy(() => import("./pages/AdminAssessments"));
const AdminCertificates      = lazy(() => import("./pages/AdminCertificates"));
const AdminSupport           = lazy(() => import("./pages/AdminSupport"));
const AdminInstructors       = lazy(() => import("./pages/AdminInstructors"));
const AdminOverview          = lazy(() => import("./pages/admin/Overview"));
const AdminUsersModule       = lazy(() => import("./pages/admin/Users"));
const AdminBundleModule      = lazy(() => import("./pages/admin/Bundle"));
const AdminAnalyticsSettingsModule = lazy(() => import("./pages/admin/AnalyticsSettings"));
const AdminAudiencesPage     = lazy(() => import("./pages/admin/Audiences"));
const AdminAffiliatePartnersPage = lazy(() => import("./pages/admin/AffiliatePartners"));
const AdminCTAAnalyticsPage  = lazy(() => import("./pages/admin/CTAAnalytics"));
const AdminNewsletterSettingsPage = lazy(() => import("./pages/admin/NewsletterSettings"));
const Newsletter             = lazy(() => import("./pages/Newsletter"));
const Organization           = lazy(() => import("./pages/Organization"));
const AdminCourseEditor      = lazy(() => import("./pages/admin/CourseEditor"));

const InstructorHome         = lazy(() => import("./pages/instructor/Home"));
const InstructorCourses      = lazy(() => import("./pages/instructor/Courses"));
const InstructorCourseEditor = lazy(() => import("./pages/instructor/CourseEditor"));
const InstructorNewCourse    = lazy(() => import("./pages/instructor/NewCourseWizard"));
const InstructorReviews      = lazy(() => import("./pages/instructor/Reviews"));
const InstructorSelfProfile  = lazy(() => import("./pages/instructor/Profile"));
const TalkToUs               = lazy(() => import("./pages/TalkToUs"));
const InstructorSetup        = lazy(() => import("./pages/InstructorSetup"));
const RequestInfo            = lazy(() => import("./pages/RequestInfo"));
const AdminLeads             = lazy(() => import("./pages/admin/Leads"));

import "./App.css";

function ChunkFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-fc-navy text-white/60" data-testid="lazy-fallback">
      <div className="w-10 h-10 border-2 border-fc-red border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Tiny inline helpers to pluck named exports off lazy modules.
function lazyNamed(getMod, name) {
  return lazy(async () => {
    const m = await getMod();
    return { default: m[name] || m.default };
  });
}
const AdminUsers              = lazyNamed(() => import("./pages/admin/Users"), "AdminUsers");
const AdminUserDetail         = lazyNamed(() => import("./pages/admin/Users"), "AdminUserDetail");
const AdminCourses            = lazyNamed(() => import("./pages/admin/Bundle"), "AdminCourses");
const AdminLiveEvents         = lazy(() => import("./pages/admin/LiveEvents"));
const CalendarPage            = lazy(() => import("./pages/CalendarPage"));
const AdminPlans              = lazyNamed(() => import("./pages/admin/Bundle"), "AdminPlans");
const AdminUpsells            = lazyNamed(() => import("./pages/admin/Bundle"), "AdminUpsells");
const AdminPromotions         = lazyNamed(() => import("./pages/admin/Bundle"), "AdminPromotions");
const AdminSponsors           = lazyNamed(() => import("./pages/admin/Bundle"), "AdminSponsors");
const AdminGamification       = lazyNamed(() => import("./pages/admin/Bundle"), "AdminGamification");
const AdminSecurity           = lazyNamed(() => import("./pages/admin/Bundle"), "AdminSecurity");
const AdminAudit              = lazyNamed(() => import("./pages/admin/Bundle"), "AdminAudit");
const AdminEmergentSync       = lazyNamed(() => import("./pages/admin/Bundle"), "AdminEmergentSync");
const AdminInstructorsFull    = lazyNamed(() => import("./pages/admin/Bundle"), "AdminInstructorsFull");
const AdminAnalytics          = lazyNamed(() => import("./pages/admin/AnalyticsSettings"), "AdminAnalytics");
const AdminSettings           = lazyNamed(() => import("./pages/admin/AnalyticsSettings"), "AdminSettings");

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-fc-navy text-white/60">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

// Phase 13.4 — gate the user-facing /certificates route behind the feature flag.
// Backend generation/admin endpoints remain available regardless.
function CertificatesGate() {
  const cfg = usePublicConfig();
  if (cfg.certificates_enabled === false) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Certificates />;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-fc-navy text-white/60">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AdminUserDetailWrapper() {
  const { userId } = useParams();
  return <AdminUserDetail userId={userId} />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <Toaster theme="dark" position="top-center" richColors />
              <ImpersonationBanner />
              <Suspense fallback={<ChunkFallback />}>
                <Routes>
            <Route path="/" element={<Home />} />
            {/* Phase 19 — /home-v2 was promoted to /. Keep this as a redirect
                for 30 days so any saved/shared preview links land on the canonical
                homepage instead of 404-ing. Remove after 2026-08-15. */}
            <Route path="/home-v2" element={<Navigate to="/" replace />} />
            <Route path="/automotive" element={<Automotive />} />
            <Route path="/entrepreneurs" element={<Entrepreneurs />} />
            <Route path="/business" element={<Business />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/courses/:slug" element={<CourseDetail />} />
            <Route path="/courses/:slug/coming-soon" element={<CourseComingSoon />} />
            <Route path="/courses/:slug/preview" element={<FreePreview />} />
            <Route path="/instructors/:id" element={<InstructorProfile />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/checkout/success" element={<RequireAuth><CheckoutSuccess /></RequireAuth>} />
            <Route path="/checkout/cancelled" element={<CheckoutCancelled />} />
            <Route path="/checkout/upsell" element={<RequireAuth><CheckoutUpsell /></RequireAuth>} />

            <Route path="/verify/:certId" element={<VerifyCertificate />} />
            <Route path="/share/cert/:certId" element={<CertificateShare />} />

            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/calendar" element={<RequireAuth><CalendarPage /></RequireAuth>} />
            <Route path="/my-courses" element={<RequireAuth><MyCourses /></RequireAuth>} />
            <Route path="/leaderboard" element={<RequireAuth><Leaderboard /></RequireAuth>} />
            <Route path="/achievements" element={<RequireAuth><Achievements /></RequireAuth>} />
            <Route path="/certificates" element={<RequireAuth><CertificatesGate /></RequireAuth>} />

            {/* Phase 4 */}
            <Route path="/community" element={<RequireAuth><Community /></RequireAuth>} />
            <Route path="/community/posts/:postId" element={<RequireAuth><CommunityPost scope="community" /></RequireAuth>} />
            <Route path="/internal" element={<RequireAuth><Internal /></RequireAuth>} />
            <Route path="/internal/posts/:postId" element={<RequireAuth><CommunityPost scope="internal" /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />

            <Route path="/learn/:courseSlug" element={<RequireAuth><CoursePlayer /></RequireAuth>} />
            <Route path="/learn/:courseSlug/complete" element={<RequireAuth><CourseComplete /></RequireAuth>} />
            <Route path="/learn/:courseSlug/:lessonId" element={<RequireAuth><CoursePlayer /></RequireAuth>} />

            {/* Phase 6 — Admin Panel (super_admin only) */}
            <Route path="/admin" element={<RequireAdmin><AdminOverview /></RequireAdmin>} />
            <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
            <Route path="/admin/users/:userId" element={<RequireAdmin><AdminUserDetailWrapper /></RequireAdmin>} />
            <Route path="/admin/instructors" element={<RequireAdmin><AdminInstructorsFull /></RequireAdmin>} />
            <Route path="/admin/courses" element={<RequireAdmin><AdminCourses /></RequireAdmin>} />
            <Route path="/admin/live-events" element={<RequireAdmin><AdminLiveEvents /></RequireAdmin>} />
            <Route path="/admin/leads" element={<RequireAdmin><AdminLeads /></RequireAdmin>} />
            <Route path="/admin/plans" element={<RequireAdmin><AdminPlans /></RequireAdmin>} />
            <Route path="/admin/upsells" element={<RequireAdmin><AdminUpsells /></RequireAdmin>} />
            <Route path="/admin/promotions" element={<RequireAdmin><AdminPromotions /></RequireAdmin>} />
            <Route path="/admin/sponsors" element={<RequireAdmin><AdminSponsors /></RequireAdmin>} />
            <Route path="/admin/gamification" element={<RequireAdmin><AdminGamification /></RequireAdmin>} />
            <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
            <Route path="/admin/security" element={<RequireAdmin><AdminSecurity /></RequireAdmin>} />
            <Route path="/admin/audit" element={<RequireAdmin><AdminAudit /></RequireAdmin>} />
            <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
            <Route path="/admin/audiences" element={<RequireAdmin><AdminAudiencesPage /></RequireAdmin>} />
            <Route path="/admin/affiliate-partners" element={<RequireAdmin><AdminAffiliatePartnersPage /></RequireAdmin>} />
            <Route path="/admin/cta-analytics" element={<RequireAdmin><AdminCTAAnalyticsPage /></RequireAdmin>} />
            <Route path="/admin/newsletter" element={<RequireAdmin><AdminNewsletterSettingsPage /></RequireAdmin>} />
            <Route path="/newsletter" element={<Newsletter />} />
            <Route path="/org" element={<RequireAuth><Organization /></RequireAuth>} />
            {/* Phase 14.5.2 — /organization alias so links from other surfaces (or tester typing /organization) resolve to the same component. */}
            <Route path="/organization" element={<RequireAuth><Organization /></RequireAuth>} />
            {/* Phase 15 — Admin course editor (modules + lessons CRUD/reorder, super_admin only) */}
            <Route path="/admin/courses/:slug/edit" element={<RequireAdmin><AdminCourseEditor /></RequireAdmin>} />
            <Route path="/admin/emergent-sync" element={<RequireAdmin><AdminEmergentSync /></RequireAdmin>} />
            {/* Carry-over from Phase 4 — Freddy Compute extensions */}
            <Route path="/admin/assessments" element={<RequireAdmin><AdminAssessments /></RequireAdmin>} />
            <Route path="/admin/certificates" element={<RequireAdmin><AdminCertificates /></RequireAdmin>} />
            <Route path="/admin/support" element={<RequireAdmin><AdminSupport /></RequireAdmin>} />
            <Route path="/admin/instructors-legacy" element={<RequireAdmin><AdminInstructors /></RequireAdmin>} />

            {/* Phase 5 Instructor portal */}
            <Route path="/instructor" element={<RequireAuth><InstructorHome /></RequireAuth>} />
            <Route path="/instructor/courses" element={<RequireAuth><InstructorCourses /></RequireAuth>} />
            <Route path="/instructor/courses/new" element={<RequireAuth><InstructorNewCourse /></RequireAuth>} />
            <Route path="/instructor/courses/:slug" element={<RequireAuth><InstructorCourseEditor /></RequireAuth>} />
            <Route path="/instructor/reviews" element={<RequireAuth><InstructorReviews /></RequireAuth>} />
            <Route path="/instructor/profile" element={<RequireAuth><InstructorSelfProfile /></RequireAuth>} />
            <Route path="/talk-to-us" element={<RequireAuth><TalkToUs /></RequireAuth>} />
            <Route path="/instructor/setup" element={<InstructorSetup />} />

            {/* Phase 14 — Standalone lead-capture landing (NOT in nav) */}
            <Route path="/request-info" element={<RequestInfo />} />

            <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
